<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import SettingsLayout from '@/Layouts/SettingsLayout.vue'
import SettingsAvailabilityScheduler from '@/Components/Forms/SettingsAvailabilityScheduler.vue'
import SettingsEditableScheduler from '@/Components/Forms/SettingsEditableScheduler.vue'
import SubjectLevelSelector from '@/Components/Forms/SubjectLevelSelector.vue'

const props = defineProps({
  user: {
    type: Object,
    required: true
  },
  groupAvailability: {
    type: Array,
    default: () => []
  },
  oneToOneAvailability: {
    type: Array,
    default: () => []
  },
  canEdit: {
    type: Boolean,
    default: false
  },
  userSubjects: {
    type: Array,
    default: () => []
  },
  userLevels: {
    type: Array,
    default: () => []
  },
  maxGroupSessionsPerCourse: {
    type: Number,
    default: 0
  },
  maxOneToOneSessions: {
    type: Number,
    default: 0
  },
  bookedGroupSlots: {
    type: Array,
    default: () => []
  },
  bookedOneToOneSlots: {
    type: Array,
    default: () => []
  }
})

// Edit mode state
const selectedGroupSlots = ref([...props.groupAvailability])
const selectedOneOnOneSlots = ref([...props.oneToOneAvailability])

const groupSlotsCount = computed(() => selectedGroupSlots.value.length)
const oneOnOneSlotsCount = computed(() => selectedOneOnOneSlots.value.length)
const totalSlotsCount = computed(() => groupSlotsCount.value + oneOnOneSlotsCount.value)

// Track whether selections have changed from initial props
const slotKey = (slot) => `${slot.day}-${slot.time}-${slot.subject_id || ''}-${slot.level_id || ''}`
const isDirty = computed(() => {
  const initialGroup = new Set(props.groupAvailability.map(slotKey))
  const currentGroup = new Set(selectedGroupSlots.value.map(slotKey))
  if (initialGroup.size !== currentGroup.size || [...initialGroup].some(k => !currentGroup.has(k))) return true

  const initialOneToOne = new Set(props.oneToOneAvailability.map(slotKey))
  const currentOneToOne = new Set(selectedOneOnOneSlots.value.map(slotKey))
  if (initialOneToOne.size !== currentOneToOne.size || [...initialOneToOne].some(k => !currentOneToOne.has(k))) return true

  return false
})
const currentSubjectLevel = ref(null)
const loading = ref(false)
const errors = ref({})
const successMessage = ref('')

// Build selectedGroupSubjects from the tutor's configured subjects and levels
// so the dropdown always shows all available options regardless of selected slots
const selectedGroupSubjects = computed(() => {
  const allLevelIds = props.userLevels.map(l => l.id)
  return props.userSubjects.map(subject => ({
    ...subject,
    level_ids: allLevelIds
  }))
})

// Clear errors when slots change
watch(selectedGroupSlots, () => {
  if (errors.value.groupAvailability) errors.value.groupAvailability = ''
})
watch(selectedOneOnOneSlots, () => {
  if (errors.value.oneOnOneAvailability) errors.value.oneOnOneAvailability = ''
})

const scrollToEl = (id) => {
  nextTick(() => {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
  })
}

const validate = () => {
  errors.value = {}

  // Validate each subject-level combo has at least one slot
  const subjectLevelSlotMap = new Map()
  selectedGroupSlots.value.forEach(slot => {
    const key = `${slot.subject_id}-${slot.level_id}`
    subjectLevelSlotMap.set(key, (subjectLevelSlotMap.get(key) || 0) + 1)
  })

  // Check max per course
  if (props.maxGroupSessionsPerCourse > 0) {
    for (const [, count] of subjectLevelSlotMap) {
      if (count > props.maxGroupSessionsPerCourse) {
        errors.value.groupAvailability = `Each course can have a maximum of ${props.maxGroupSessionsPerCourse} session(s). Please remove some slots.`
        return false
      }
    }
  }

  // Check 1:1 max
  if (props.maxOneToOneSessions > 0 && selectedOneOnOneSlots.value.length > props.maxOneToOneSessions) {
    errors.value.oneOnOneAvailability = `You can select a maximum of ${props.maxOneToOneSessions} 1:1 session slot(s).`
    return false
  }

  return true
}

const saveAvailability = async () => {
  if (!validate()) return

  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    const response = await fetch('/teacher/settings/availability', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify({
        group_sessions: selectedGroupSlots.value,
        one_on_one_sessions: selectedOneOnOneSlots.value
      })
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        errors.value = Object.keys(data.errors).reduce((acc, key) => {
          acc[key] = Array.isArray(data.errors[key]) ? data.errors[key][0] : data.errors[key]
          return acc
        }, {})
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      scrollToEl('availability-error-banner')
    } else {
      successMessage.value = 'Availability updated successfully!'
      scrollToEl('availability-success-banner')
      setTimeout(() => { successMessage.value = '' }, 3000)
    }
  } catch {
    errors.value = { general: 'An error occurred. Please try again.' }
    scrollToEl('availability-error-banner')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <SettingsLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" id="availability-success-banner" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">check_circle</span>
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- General Error Message -->
      <div v-if="errors.general" id="availability-error-banner" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">error</span>
        </div>
        <div>
          <p class="text-red-900 font-semibold">Error</p>
          <p class="text-red-700 text-sm">{{ errors.general }}</p>
        </div>
      </div>

      <!-- Info Banner -->
      <div class="bg-gradient-to-br from-teal-50 to-teal-100 rounded-2xl p-6 border border-blue-100">
        <div class="flex items-start gap-4">
          <div class="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center shrink-0">
            <span class="material-symbols-outlined text-white text-xl">{{ canEdit ? 'edit_calendar' : 'info' }}</span>
          </div>
          <div class="flex-1">
            <h2 class="text-xl font-bold text-gray-900 mb-2">Your Weekly Availability</h2>
            <p class="text-gray-700 leading-relaxed mb-3">
              <template v-if="canEdit">
                Manage your availability for group sessions and 1:1 tutoring. Select time slots below and click save when you're done.
              </template>
              <template v-else>
                This is a view-only display of your availability settings. The highlighted time slots show when you're available for group sessions and 1:1 tutoring.
              </template>
            </p>
            <div class="flex items-center gap-4 text-sm">
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-blue-600">calendar_today</span>
                <span class="font-medium text-gray-900">Total Slots: <span class="text-blue-600">{{ totalSlotsCount }}</span></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-purple-600">groups</span>
                <span class="font-medium text-gray-900">Group: <span class="text-purple-600">{{ groupSlotsCount }}</span></span>
              </div>
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-yellow-600">person</span>
                <span class="font-medium text-gray-900">1:1: <span class="text-yellow-600">{{ oneOnOneSlotsCount }}</span></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- No Availability Message -->
      <div v-if="totalSlotsCount === 0 && !canEdit" class="bg-amber-50 border-2 border-amber-200 rounded-2xl p-6 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">schedule</span>
        </div>
        <div>
          <p class="text-amber-900 font-semibold">No Availability Set</p>
          <p class="text-amber-700 text-sm">You haven't set any availability slots yet. This will be configured during your onboarding process.</p>
        </div>
      </div>

      <!-- ==================== EDIT MODE ==================== -->
      <template v-if="canEdit">
        <!-- Bindle Live sessions -->
        <div class="bg-white rounded-2xl p-6 border border-gray-200">
          <!-- Group Error -->
          <div v-if="errors.groupAvailability" class="mb-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
            <div class="flex items-start gap-3">
              <span class="material-symbols-outlined shrink-0 text-[1.25rem] leading-5 text-danger">error</span>
              <p class="text-red-600 font-medium text-sm">{{ errors.groupAvailability }}</p>
            </div>
          </div>

          <!-- Header -->
          <div class="flex items-center gap-3 mb-4">
            <div class="w-10 h-10 rounded-lg flex items-center justify-center bg-purple-100">
              <span class="material-symbols-outlined text-purple-600">groups</span>
            </div>
            <div>
              <h4 class="font-semibold text-gray-900">Bindle Live sessions</h4>
              <p class="text-xs text-gray-500">{{ maxGroupSessionsPerCourse > 0 ? `Select up to ${maxGroupSessionsPerCourse} time slot(s) per course for weekly group sessions` : 'Select time slots for weekly group sessions' }}</p>
            </div>
          </div>

          <!-- Subject-Level Selector -->
          <SubjectLevelSelector
            :selected-subjects="selectedGroupSubjects"
            :levels="userLevels"
            :selected-subject-level="currentSubjectLevel"
            @update:selected-subject-level="currentSubjectLevel = $event"
          />

          <SettingsEditableScheduler
            title=""
            description=""
            icon="groups"
            icon-bg-color="bg-purple-100"
            icon-text-color="text-purple-600"
            :selected-slots="selectedGroupSlots"
            @update:selected-slots="selectedGroupSlots = $event"
            :disabled-slots="selectedOneOnOneSlots"
            :locked-slots="bookedGroupSlots"
            :max-slots-per-group="maxGroupSessionsPerCourse"
            :multi-select="true"
            selected-color="live"
            :is-group-calendar="true"
            :current-subject-level="currentSubjectLevel"
            :show-slot-details="true"
            :hide-header="true"
            :subjects="selectedGroupSubjects"
          />

          <div class="mt-4 pt-4 border-t border-gray-100">
            <p class="text-sm text-gray-500">
              <span class="font-semibold text-purple-600">{{ groupSlotsCount }}</span> time slot{{ groupSlotsCount !== 1 ? 's' : '' }} for group sessions
            </p>
          </div>
        </div>

        <!-- 1:1 Tutoring Sessions -->
        <div class="bg-white rounded-2xl p-6 border border-gray-200">
          <!-- 1:1 Error -->
          <div v-if="errors.oneOnOneAvailability" class="mb-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
            <div class="flex items-start gap-3">
              <span class="material-symbols-outlined shrink-0 text-[1.25rem] leading-5 text-danger">error</span>
              <p class="text-red-600 font-medium text-sm">{{ errors.oneOnOneAvailability }}</p>
            </div>
          </div>

          <SettingsEditableScheduler
            title="1:1 Tutoring Sessions"
            :description="maxOneToOneSessions > 0 ? `Select up to ${maxOneToOneSessions} time slot(s) for individual tutoring` : 'Select available time slots for individual tutoring (excluding group session times)'"
            icon="person"
            icon-bg-color="bg-yellow-100"
            icon-text-color="text-yellow-600"
            :selected-slots="selectedOneOnOneSlots"
            @update:selected-slots="selectedOneOnOneSlots = $event"
            :disabled-slots="selectedGroupSlots"
            :locked-slots="bookedOneToOneSlots"
            :multi-select="true"
            selected-color="brand"
            :show-slot-details="true"
            :max-total-slots="maxOneToOneSessions"
          />

          <div class="mt-4 pt-4 border-t border-gray-100">
            <p class="text-sm text-gray-500">
              <span class="font-semibold text-yellow-600">{{ oneOnOneSlotsCount }}</span> time slot{{ oneOnOneSlotsCount !== 1 ? 's' : '' }} for 1:1 sessions
            </p>
          </div>
        </div>

        <!-- Save Button -->
        <div class="flex justify-end">
          <button
            @click="saveAvailability"
            :disabled="loading || !isDirty"
            class="px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <span v-if="loading" class="material-symbols-outlined animate-spin text-lg">progress_activity</span>
            <span>{{ loading ? 'Saving...' : 'Save Availability' }}</span>
          </button>
        </div>
      </template>

      <!-- ==================== VIEW-ONLY MODE ==================== -->
      <template v-else>
        <!-- Group Sessions Scheduler -->
        <div v-if="groupSlotsCount > 0" class="bg-white rounded-2xl p-6 border border-gray-200">
          <SettingsAvailabilityScheduler
            :selected-slots="groupAvailability"
            title="Group Session Availability"
            description="Your available time slots for group tutoring sessions (max 5 students per session)"
            icon="groups"
            icon-bg-color="bg-purple-100"
            icon-text-color="text-purple-600"
            selected-color="live"
            :view-only="true"
            :is-group-calendar="true"
          />
          <div class="mt-4 pt-4 border-t border-gray-100">
            <p class="text-sm text-gray-500">
              You have <span class="font-semibold text-purple-600">{{ groupSlotsCount }}</span> time slot{{ groupSlotsCount !== 1 ? 's' : '' }} available for group sessions
            </p>
          </div>
        </div>

        <!-- 1:1 Sessions Scheduler -->
        <div v-if="oneOnOneSlotsCount > 0" class="bg-white rounded-2xl p-6 border border-gray-200">
          <SettingsAvailabilityScheduler
            :selected-slots="oneToOneAvailability"
            title="1:1 Session Availability"
            description="Your available time slots for one-on-one tutoring sessions (personalized attention)"
            icon="person"
            icon-bg-color="bg-yellow-100"
            icon-text-color="text-yellow-600"
            selected-color="brand"
            :view-only="true"
          />
          <div class="mt-4 pt-4 border-t border-gray-100">
            <p class="text-sm text-gray-500">
              You have <span class="font-semibold text-yellow-600">{{ oneOnOneSlotsCount }}</span> time slot{{ oneOnOneSlotsCount !== 1 ? 's' : '' }} available for 1:1 sessions
            </p>
          </div>
        </div>

        <!-- Help Section -->
        <div class="bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl p-6 border border-gray-200">
          <div class="flex items-start gap-4">
            <div class="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center shrink-0">
              <span class="material-symbols-outlined text-white text-lg">help</span>
            </div>
            <div>
              <h4 class="font-semibold text-gray-900 mb-2">Need to Update Your Availability?</h4>
              <p class="text-sm text-gray-600 leading-relaxed">
                To modify your availability schedule, please contact support or update it through your tutor application settings.
              </p>
            </div>
          </div>
        </div>
      </template>
    </div>
  </SettingsLayout>
</template>
